/**
 * MetricEntryStacked — Option C data-entry layout (DEV-1201,
 * POLICY_ACTION_CENTER.md). A drop-in alternative to MetricEntryGrid with the
 * SAME props/contract, but rendered as stacked stat-cell cards built for a
 * narrow tray column instead of a wide inline table.
 *
 * Per metric: a card with Current / Target stat cells + a New-value input
 * (slider for percentage, Yes/No for binary), an optional note, and a shared
 * reporting-date + Save All footer. The host's onSave handler is identical to
 * the one MetricEntryGrid uses, so the wrappers (Mission/Budget/Schedule) can
 * switch layouts via a `variant` prop without changing save logic.
 */

import React, { useCallback, useMemo, useState } from "react";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import type {
  MetricEntryGridProps,
  MetricEntryValue,
  MetricEntrySavePayload,
  MetricValueType,
} from "./MetricEntryGrid";
import styles from "./MetricEntryStacked.module.css";

function todayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;
}

function formatDisplay(
  v: number | null | undefined,
  valueType?: MetricValueType,
  milestoneTotal?: number,
): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return "—";
  switch (valueType) {
    case "binary":
      return v >= 1 ? "Yes" : "No";
    case "percentage":
      return `${v.toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;
    case "milestone_set":
      return `${v} of ${milestoneTotal ?? "?"}`;
    default:
      return v.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }
}

const MetricEntryStacked: React.FC<MetricEntryGridProps> = ({
  metrics,
  onSave,
  defaultDate,
  readOnly = false,
  title,
  hideDateSelector = false,
  reportingDate: externalDate,
  onDateChange,
}) => {
  const { t } = useAppTranslation("kpi");

  const [internalDate, setInternalDate] = useState<string>(defaultDate || todayISO());
  const reportingDate = externalDate ?? internalDate;
  const handleDateChange = useCallback(
    (val: string) => {
      if (onDateChange) onDateChange(val);
      else setInternalDate(val);
    },
    [onDateChange],
  );

  const metricKeysSig = metrics.map((m) => m.key).join(",");
  const initialEntries = useMemo(
    () => metrics.map((m): MetricEntryValue => ({ key: m.key, value: "", note: "", dirty: false })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [metricKeysSig],
  );
  const [entries, setEntries] = useState<MetricEntryValue[]>(initialEntries);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<{ type: "success" | "error"; message: string } | null>(null);

  React.useEffect(() => {
    setEntries((prev) => {
      const byKey = new Map(prev.map((e) => [e.key, e]));
      return metrics.map((m) => byKey.get(m.key) || { key: m.key, value: "", note: "", dirty: false });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metricKeysSig]);

  const updateEntry = useCallback((key: string, field: "value" | "note", val: string) => {
    setEntries((prev) => prev.map((e) => (e.key === key ? { ...e, [field]: val, dirty: true } : e)));
    setStatus(null);
  }, []);

  // milestone_set: track which checklist items are checked per metric. The
  // model stores a COUNT (not which items), so arbitrary check/uncheck maps to
  // a number. Item labels are generic until the data model carries per-item
  // milestone labels (follow-up).
  const [checkedSets, setCheckedSets] = useState<Record<string, Set<number>>>({});
  const getChecked = useCallback(
    (key: string, currentValue: number | null | undefined): Set<number> => {
      if (checkedSets[key]) return checkedSets[key];
      const n = Math.max(0, Math.round(currentValue ?? 0));
      return new Set(Array.from({ length: n }, (_, i) => i));
    },
    [checkedSets],
  );
  const toggleMilestone = useCallback(
    (key: string, idx: number, currentValue: number | null | undefined) => {
      const next = new Set(getChecked(key, currentValue));
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      setCheckedSets((prev) => ({ ...prev, [key]: next }));
      updateEntry(key, "value", String(next.size));
    },
    [getChecked, updateEntry],
  );

  const dirtyEntries = useMemo(
    () => entries.filter((e) => e.dirty && e.value.trim() !== ""),
    [entries],
  );

  const handleSave = useCallback(async () => {
    if (dirtyEntries.length === 0) return;
    setSaving(true);
    setStatus(null);
    const payload: MetricEntrySavePayload[] = dirtyEntries
      .map((e) => ({ key: e.key, value: parseFloat(e.value), note: e.note.trim(), reportingDate }))
      .filter((p) => Number.isFinite(p.value));
    if (payload.length === 0) {
      setStatus({ type: "error", message: t("dataEntry.invalidValues") || "Please enter valid numbers." });
      setSaving(false);
      return;
    }
    try {
      await onSave(payload);
      setEntries((prev) => prev.map((e) => ({ ...e, value: "", note: "", dirty: false })));
      setStatus({
        type: "success",
        message: t("dataEntry.saveSuccess", { count: payload.length }) || `${payload.length} value(s) saved.`,
      });
    } catch (err: any) {
      setStatus({
        type: "error",
        message: `${t("dataEntry.saveError") || "Save failed."} ${err?.message || "Unknown error"}`,
      });
    } finally {
      setSaving(false);
    }
  }, [dirtyEntries, reportingDate, onSave, t]);

  const handleCancel = useCallback(() => {
    setEntries(initialEntries);
    setStatus(null);
  }, [initialEntries]);

  if (metrics.length === 0) {
    return <div className={styles.empty}>{t("dataEntry.noMetrics") || "No metrics configured."}</div>;
  }

  return (
    <div className={styles.wrap}>
      <div className={styles.header}>
        <div className={styles.title}>{title || t("dataEntry.title") || "Enter Data"}</div>
        {!hideDateSelector && (
          <div className={styles.period}>
            <label className={styles.periodLabel} htmlFor="mes-date">
              {t("dataEntry.reportingDate") || "Reporting date"}:
            </label>
            <input
              id="mes-date"
              type="date"
              className={styles.dateInput}
              value={reportingDate}
              onChange={(e) => handleDateChange(e.target.value)}
              disabled={readOnly}
            />
          </div>
        )}
      </div>

      {metrics.map((m) => {
        const entry = entries.find((e) => e.key === m.key);
        const vt: MetricValueType = m.valueType ?? "numeric";
        // Sliders default to the current value (and the manual number mirrors
        // it). "eff" is the value both controls display until the user edits.
        const eff =
          entry?.value && entry.value !== ""
            ? entry.value
            : m.currentValue != null
              ? String(m.currentValue)
              : "0";
        return (
          <div key={m.key} className={styles.card}>
            <div className={styles.name}>
              {entry?.dirty && <span className={styles.dirty} aria-hidden="true" />}
              <span>{m.label}</span>
              {m.unit && <span className={styles.unit}>({m.unit})</span>}
            </div>
            <div className={styles.cells}>
              <div className={styles.cell}>
                <div className={styles.cellKey}>{t("dataEntry.colCurrent") || "Current"}</div>
                <div className={styles.cellVal}>{formatDisplay(m.currentValue, vt, m.milestoneTotal)}</div>
              </div>
              <div className={styles.cell}>
                <div className={styles.cellKey}>{t("dataEntry.colTarget") || "Target"}</div>
                <div className={styles.cellVal}>{formatDisplay(m.targetValue, vt, m.milestoneTotal)}</div>
              </div>
            </div>
            <div className={styles.newField}>
              <div className={styles.cellKey}>{t("dataEntry.colNewValue") || "New value"}</div>
              {vt === "binary" ? (
                  <select
                    className={styles.input}
                    value={entry?.value ?? ""}
                    onChange={(e) => updateEntry(m.key, "value", e.target.value)}
                    disabled={readOnly || saving}
                    aria-label={`${m.label} value`}
                  >
                    <option value="">—</option>
                    <option value="1">{t("dataEntry.binaryYes") || "Yes"}</option>
                    <option value="0">{t("dataEntry.binaryNo") || "No"}</option>
                  </select>
                ) : vt === "percentage" ? (
                  <div className={styles.pctWrap}>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      step="1"
                      style={{ flex: 1 }}
                      value={eff}
                      onChange={(e) => updateEntry(m.key, "value", e.target.value)}
                      disabled={readOnly || saving}
                      aria-label={`${m.label} value`}
                    />
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="1"
                      className={styles.pctNum}
                      value={eff}
                      onChange={(e) => updateEntry(m.key, "value", e.target.value)}
                      disabled={readOnly || saving}
                      aria-label={`${m.label} value (manual entry)`}
                    />
                    <span className={styles.pctUnit}>%</span>
                  </div>
                ) : vt === "milestone_set" && (m.milestoneTotal ?? 0) > 0 ? (
                  <div className={styles.checklist}>
                    {Array.from({ length: m.milestoneTotal ?? 0 }).map((_, i) => (
                      <label key={i} className={styles.checkItem}>
                        <input
                          type="checkbox"
                          checked={getChecked(m.key, m.currentValue).has(i)}
                          onChange={() => toggleMilestone(m.key, i, m.currentValue)}
                          disabled={readOnly || saving}
                        />
                        <span>{t("dataEntry.milestoneItem", { n: i + 1 })}</span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <input
                    type="number"
                    step="any"
                    className={styles.input}
                    placeholder="—"
                    value={entry?.value ?? ""}
                    onChange={(e) => updateEntry(m.key, "value", e.target.value)}
                    disabled={readOnly || saving}
                    aria-label={`${m.label} value`}
                  />
                )}
              </div>
            {!readOnly && (
              <input
                type="text"
                className={styles.note}
                placeholder={t("dataEntry.notePlaceholder") || "Optional note"}
                value={entry?.note ?? ""}
                onChange={(e) => updateEntry(m.key, "note", e.target.value)}
                disabled={saving}
                aria-label={`${m.label} note`}
              />
            )}
          </div>
        );
      })}

      {!readOnly && (
        <div className={styles.footer}>
          <span
            className={
              status?.type === "success"
                ? styles.statusSuccess
                : status?.type === "error"
                  ? styles.statusError
                  : styles.status
            }
          >
            {status
              ? status.message
              : dirtyEntries.length > 0
                ? `${dirtyEntries.length} ${t("dataEntry.pendingChanges") || "value(s) to save"}`
                : ""}
          </span>
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.btn}
              onClick={handleCancel}
              disabled={saving || dirtyEntries.length === 0}
            >
              {t("dataEntry.cancel") || "Cancel"}
            </button>
            <button
              type="button"
              className={`${styles.btn} ${styles.btnSave}`}
              onClick={handleSave}
              disabled={saving || dirtyEntries.length === 0}
            >
              {saving ? t("dataEntry.saving") || "Saving…" : t("dataEntry.saveAll") || "Save All"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MetricEntryStacked;
